import axios, { AxiosResponse } from 'axios';

// Konfiguracja bazowego URL dla API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** Czytelny komunikat zamiast surowych kodów Node (ECONNRESET itd.) — snackbar / UI. */
export function humanizeApiNetworkError(error: unknown): string {
  const e = error as { code?: string; message?: string };
  const code = String(e?.code || '').toUpperCase();
  const msg = String(e?.message || '');
  const blob = `${code} ${msg}`.toLowerCase();
  if (blob.includes('econnreset') || blob.includes('socket hang up') || blob.includes('epipe')) {
    return 'Połączenie z serwerem zostało zerwane. Sprawdź, czy backend działa, i spróbuj ponownie.';
  }
  if (blob.includes('econnrefused')) {
    return 'Brak połączenia z serwerem. Uruchom backend lub sprawdź adres API (REACT_APP_API_URL).';
  }
  if (blob.includes('etimedout') || blob.includes('timeout') || code === 'ECONNABORTED') {
    return 'Przekroczony czas oczekiwania na odpowiedź serwera. Spróbuj ponownie.';
  }
  if (blob.includes('enotfound') || blob.includes('getaddrinfo')) {
    return 'Nie udało się znaleźć serwera API. Sprawdź adres sieciowy i DNS.';
  }
  if (msg === 'Network Error' || blob.includes('network error')) {
    return 'Błąd sieci — brak odpowiedzi z serwera. Sprawdź połączenie i czy backend jest uruchomiony.';
  }
  return msg || 'Nieznany błąd połączenia.';
}

function applyHumanNetworkMessage(error: any): void {
  if (!error || error.response) return;
  const friendly = humanizeApiNetworkError(error);
  const raw = String(error.message || '');
  if (
    friendly &&
    friendly !== raw &&
    /econnreset|econnrefused|etimedout|enotfound|econnaborted|epipe|network error|socket hang up|getaddrinfo/i.test(
      `${error.code || ''} ${raw}`
    )
  ) {
    error.userMessage = friendly;
    error.message = friendly;
  }
}

// Utworzenie instancji axios z domyślną konfiguracją
const api = axios.create({
  baseURL: API_BASE_URL,
  // Nie ustawiaj globalnie Content-Type – dla FormData musi być automatyczny boundary
  headers: {},
  withCredentials: true, // Ważne dla obsługi sesji/ciasteczek, jeśli są używane równolegle
});

// Interceptor do dodawania tokenu JWT do nagłówka Authorization
api.interceptors.request.use(
  (config: any): any => {
    // Jeśli wysyłamy FormData, usuń Content-Type, żeby przeglądarka dodała boundary
    if (config?.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
    } else {
      // Dla zwykłych JSON-ów ustaw Content-Type jeśli brak
      if (config.headers && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    // Pobierz token JWT z localStorage (zapisywany przez AuthContext)
    const token = localStorage.getItem('auth_token'); // Zmieniono klucz na 'auth_token'
    
    if (token) {
      // Upewnij się, że headers istnieje (może być undefined dla FormData)
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Authorization'] = `Bearer ${token}`;
      // console.debug('JWT attached');
    } else {
      // console.debug('Brak tokenu – request bez autoryzacji');
    }

    // Ogranicz logi interceptora – usuń głośne konsolowe logi
    return config;
  },
  (error: any): Promise<any> => {
    console.error('Błąd podczas konfiguracji żądania:', error);
    return Promise.reject(error);
  }
);

// Interceptor dla odpowiedzi
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Wyłącz głośne logowanie odpowiedzi
    
    // W przypadku statusu 304, axios może zwrócić pusty obiekt data.
    // Upewniamy się, że zwracamy obiekt odpowiedzi, nawet jeśli jest to 304.
    // projectService powinien obsłużyć status 304.
    return response;
  },
  (error: any): Promise<any> => {
    // Obsługa błędów odpowiedzi
    if (error.response) {
      // Serwer zwrócił odpowiedź z kodem statusu innym niż 2xx
      // console.warn('[api] response error', error.response.status, error.response.statusText);
      
      // Sprawdź, czy błąd dotyczy autoryzacji
      const suppress = Boolean(error?.config?.suppressAuthErrorLog);
      if (error.response.status === 401) {
        if (!suppress) console.error('[api.ts interceptor response] Błąd autoryzacji - nieprawidłowy lub wygasły token');
        // Można dodać logikę przekierowania do strony logowania
      } else if (error.response.status === 403) {
        if (!suppress) console.error('[api.ts interceptor response] Błąd autoryzacji - brak uprawnień');
      }
      
      // Przekazujemy obiekt błędu z odpowiedzią serwera
      return Promise.reject(error); 

    } else if (error.request) {
      // Żądanie zostało wysłane, ale nie otrzymano odpowiedzi (np. problem z siecią)
      applyHumanNetworkMessage(error);
      return Promise.reject(error);

    } else {
      // Coś poszło nie tak podczas konfiguracji żądania (często ECONNRESET / reset po stronie Node)
      applyHumanNetworkMessage(error);
      return Promise.reject(error);
    }
  }
);

export default api;
